import { Pagination } from "services/search";

import {
  ContractSaleSearchOptions,
  ContractRentSearchOptions,
  ContractSaleStatus,
  ContractRentStatus,
  FleetVehicleContractSale,
  FleetVehicleContractSaleEditFormValues,
  FleetVehicleContractSaleRow,
  FleetVehicleContractRent,
  FleetVehicleContractRentEditFormValues,
  FleetVehicleContractRentRow,
} from "./types";

import { AvailabilityResult, DataWithPagination, transformAvailabilityResult } from "services/api/types";
import {
  transformContractSaleStatus,
  transformFleetVehicleContractSale,
  transformFleetVehicleContractSaleOnCreate,
  transformFleetVehicleContractSaleOnUpdate,
  transformFleetVehicleContractSaleRow,
  transformContractRentStatus,
  transformFleetVehicleContractRent,
  transformFleetVehicleContractRentOnCreate,
  transformFleetVehicleContractRentOnUpdate,
  transformFleetVehicleContractRentRow,
} from "./transformations";
import { createApiCall, paginatedApiCall } from "services/api/createApiCall";
import { transformErrorResponse } from "services/api/transformation";
import { DropdownCacheKey, invalidateDropdownCache } from "services/hooks/useDropdownCache";
import { filesApi } from "services/api/config";
import { getAccessToken } from "services/auth";
import { showNotification } from "services/api/snackbar/index";
import { EmailSendData } from "ui/components/Modal/EmailSendModal/types";
import { InvoiceOutgoingItemRow } from "services/api/fleet/vehicles/invoices/types";
import { transformInvoicePrefillItemRow } from "services/api/fleet/vehicles/invoices/transformations";

/**
 * Slanje ugovora e-mailom. PDF ugovora dodaje backend, ovdje idu samo dodatni privici
 * koje je korisnik odabrao u modalu.
 */
const sendContractEmail = async (
  path: string,
  emailData: EmailSendData
): Promise<boolean> => {
  const formData = new FormData();

  emailData.recipients.forEach((recipient) => formData.append("Recipients", recipient));
  formData.append("ReplyTo", emailData.replyTo);
  formData.append("Subject", emailData.subject);
  formData.append("Content", emailData.content);

  if (emailData.cc) {
    formData.append("CC", emailData.cc);
  }

  for (let index = 0; index < emailData.attachments.length; index++) {
    const file = emailData.attachments[index];
    formData.append(`Attachments[${index}].Id`, file.id);
    formData.append(`Attachments[${index}].Title`, file.title ?? file.name);
    formData.append(`Attachments[${index}].IsNew`, String(file.isNew ?? false));

    if (file.url) {
      formData.append(`Attachments[${index}].Url`, file.url);
      const fileResponse = await fetch(file.url);
      const blob = await fileResponse.blob();
      formData.append(`Attachments[${index}].File`, blob, file.name);
    }
  }

  const config = {
    headers: { Authorization: `Bearer ${getAccessToken()}` },
  };

  const response = await filesApi.post(path, formData, config);

  const sent: boolean = response.data?.sent ?? false;

  if (sent) {
    showNotification("Ugovor je poslan na e-mail", { variant: "success" });
  } else {
    showNotification("Slanje ugovora na e-mail nije uspjelo", { variant: "error" });
  }

  return sent;
};

/*---------------------------------------------------------------------------------------
   GET - CONTRACT SALE STATUSES
-----------------------------------------------------------------------------------------*/
export const getContractSaleStatusesAPI = async (): Promise<ContractSaleStatus[]> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/contracts/sale/statuses`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return response.data?.map(transformContractSaleStatus) ?? [];
};

/*---------------------------------------------------------------------------------------
   SEARCH - VEHICLE - CONTRACT - SALE - GENERATE CONTRACT NUMBER
-----------------------------------------------------------------------------------------*/
export const getContractSaleGenerateNumberAPI = async (
): Promise<string> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/contracts/sale/generatenumber`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return response.data?.contractNumber;
};

/*---------------------------------------------------------------------------------------
   GET - CONTRACT SALE - IS AVAILABLE
-----------------------------------------------------------------------------------------*/
export const getContractSaleIsAvailableAPI = async (
  vehicleId?: string | null
): Promise<AvailabilityResult> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/contracts/sale/isavailable${vehicleId ? `?vehicleId=${vehicleId}` : ""}`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformAvailabilityResult(response.data);
};

/*---------------------------------------------------------------------------------------
   SEARCH - VEHICLE - INFO
-----------------------------------------------------------------------------------------*/
export const getFleetVehicleContractSaleAPI = async (
  id: string
): Promise<FleetVehicleContractSale> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/contracts/sale/${id}`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformFleetVehicleContractSale(response.data);
};

/*---------------------------------------------------------------------------------------
  SEARCH - VEHICLE - CONTRACT
-----------------------------------------------------------------------------------------*/
export const getFleetVehicleContractSalesAPI = async (
  pagination: Pagination,
  options?: ContractSaleSearchOptions,
): Promise<DataWithPagination<FleetVehicleContractSaleRow>> => {
  let path = `/v1/fleet/vehicles/contracts/sale?`;

  if (options?.vehicleId) path += `vehicleId=${options.vehicleId}`;
  if (options?.contractSaleStatusId) path += `&contractSaleStatusId=${options.contractSaleStatusId}`;
  if (options?.search) path += `&search=${encodeURIComponent(options.search)}`;
  if (options?.showLinkedToInvoice === false) path += `&showLinkedToInvoice=false`;
  if (options?.showCancelled === false) path += `&showCancelled=false`;
  if (options?.contractDateFrom) path += `&contractDateFrom=${new Date(options.contractDateFrom).toISOString()}`;
  if (options?.contractDateTo) path += `&contractDateTo=${new Date(options.contractDateTo).toISOString()}`;
  if (options?.sellingPriceMin) path += `&sellingPriceMin=${options.sellingPriceMin}`;
  if (options?.sellingPriceMax) path += `&sellingPriceMax=${options.sellingPriceMax}`;
  if (options?.contactClientId) path += `&contactClientId=${options.contactClientId}`;

  return await paginatedApiCall(
    path,
    pagination,
    transformFleetVehicleContractSaleRow
  );
};

/*---------------------------------------------------------------------------------------
  CREATE - VEHICLE - CONTRACT
-----------------------------------------------------------------------------------------*/
export const createFleetVehicleContractSaleAPI = async (
  vehicleId: string,
  formValues: FleetVehicleContractSaleEditFormValues,
  t: (key: string) => string
): Promise<FleetVehicleContractSale> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/${vehicleId}/contracts/sale`,
      method: "POST",
      body: transformFleetVehicleContractSaleOnCreate(formValues, vehicleId),
    },
    {
      getMessage: {
        success: () =>
          `${t("admin.fleet.vehicle.contract.sale.create.message.success")}`,
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  console.log('response', response)

  invalidateDropdownCache(DropdownCacheKey.FleetContractSales);
  return transformFleetVehicleContractSale(response.data);
};

/*---------------------------------------------------------------------------------------
  EDIT - VEHICLE - CONTRACT
-----------------------------------------------------------------------------------------*/
export const updateFleetVehicleContractSaleAPI = async (
  id: string,
  vehicleId: string,
  formValues: FleetVehicleContractSaleEditFormValues,
  t: (key: string) => string
): Promise<FleetVehicleContractSale> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/${vehicleId}/contracts/sale/${id}`,
      method: "PUT",
      body: transformFleetVehicleContractSaleOnUpdate(formValues, vehicleId),
    },
    {
      getMessage: {
       success: () =>
          `${t("admin.fleet.vehicle.contract.sale.edit.message.success")}`,
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  invalidateDropdownCache(DropdownCacheKey.FleetContractSales);
  return transformFleetVehicleContractSale(response.data);
};

/*---------------------------------------------------------------------------------------
  DELETE - VEHICLE - CONTRACT
-----------------------------------------------------------------------------------------*/
export const deleteFleetVehicleContractSaleAPI = async (
  vehicleId: string,
  contractId: string,
  t: (key: string) => string
): Promise<boolean> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/${vehicleId}/contracts/sale/${contractId}`,
      method: "DELETE",
    },
    {
      getMessage: {
        success: () => t("admin.fleet.vehicle.contract.message.delete"),
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  invalidateDropdownCache(DropdownCacheKey.FleetContractSales);
  return response.data?.deleted;
};

/*---------------------------------------------------------------------------------------
   PREVIEW - PDF
-----------------------------------------------------------------------------------------*/
export const getFleetVehicleContractSalePdfPreviewAPI = async (
  contractId: string
): Promise<string | null> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/contracts/sale/${contractId}/pdf/preview`,
      method: "GET",
      responseType: "blob",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  if (!response?.data) return null;

  const blob = new Blob([response.data], { type: "application/pdf" });
  return URL.createObjectURL(blob);
};

/*---------------------------------------------------------------------------------------
  TOGGLE - CONTRACT SALE STATUS HISTORY
-----------------------------------------------------------------------------------------*/
export const toggleContractSaleStatusHistoryAPI = async (
  contractId: string,
  statusId: string,
  checked: boolean
): Promise<FleetVehicleContractSale> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/contracts/sale/${contractId}/status-history/${statusId}`,
      method: "PUT",
      body: { checked },
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformFleetVehicleContractSale(response.data);
};

/*---------------------------------------------------------------------------------------
  SEND - CONTRACT SALE - EMAIL
-----------------------------------------------------------------------------------------*/
export const sendFleetVehicleContractSaleEmailAPI = async (
  contractId: string,
  emailData: EmailSendData
): Promise<boolean> =>
  sendContractEmail(`/v1/fleet/vehicles/contracts/sale/${contractId}/send-email`, emailData);


/*=========================================================================================
   CONTRACT RENT API
=========================================================================================*/

/*---------------------------------------------------------------------------------------
   GET - CONTRACT RENT STATUSES
-----------------------------------------------------------------------------------------*/
export const getContractRentStatusesAPI = async (): Promise<ContractRentStatus[]> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/contracts/rent/statuses`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return response.data?.map(transformContractRentStatus) ?? [];
};

/*---------------------------------------------------------------------------------------
   GET - CONTRACT RENT - GENERATE CONTRACT NUMBER
-----------------------------------------------------------------------------------------*/
export const getContractRentGenerateNumberAPI = async (
): Promise<string> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/contracts/rent/generatenumber`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return response.data?.contractNumber;
};

/*---------------------------------------------------------------------------------------
   GET - CONTRACT RENT - IS AVAILABLE
-----------------------------------------------------------------------------------------*/
export const getContractRentIsAvailableAPI = async (
  vehicleId?: string | null
): Promise<AvailabilityResult> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/contracts/rent/isavailable${vehicleId ? `?vehicleId=${vehicleId}` : ""}`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformAvailabilityResult(response.data);
};

/*---------------------------------------------------------------------------------------
   GET - VEHICLE - CONTRACT RENT
-----------------------------------------------------------------------------------------*/
export const getFleetVehicleContractRentAPI = async (
  id: string
): Promise<FleetVehicleContractRent> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/contracts/rent/${id}`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformFleetVehicleContractRent(response.data);
};

/*---------------------------------------------------------------------------------------
  SEARCH - VEHICLE - CONTRACT RENTS
-----------------------------------------------------------------------------------------*/
export const getFleetVehicleContractRentsAPI = async (
  pagination: Pagination,
  options?: ContractRentSearchOptions,
): Promise<DataWithPagination<FleetVehicleContractRentRow>> => {
  let path = `/v1/fleet/vehicles/contracts/rent?`;

  if (options?.vehicleId) path += `vehicleId=${options.vehicleId}`;
  if (options?.contractRentStatusId) path += `&contractRentStatusId=${options.contractRentStatusId}`;
  if (options?.search) path += `&search=${encodeURIComponent(options.search)}`;
  if (options?.showLinkedToInvoice === false) path += `&showLinkedToInvoice=false`;
  if (options?.showCancelled === false) path += `&showCancelled=false`;
  if (options?.contractDateFrom) path += `&contractDateFrom=${new Date(options.contractDateFrom).toISOString()}`;
  if (options?.contractDateTo) path += `&contractDateTo=${new Date(options.contractDateTo).toISOString()}`;
  if (options?.sellingPriceMin) path += `&sellingPriceMin=${options.sellingPriceMin}`;
  if (options?.sellingPriceMax) path += `&sellingPriceMax=${options.sellingPriceMax}`;
  if (options?.contactClientId) path += `&contactClientId=${options.contactClientId}`;

  return await paginatedApiCall(
    path,
    pagination,
    transformFleetVehicleContractRentRow
  );
};

/*---------------------------------------------------------------------------------------
  CREATE - VEHICLE - CONTRACT RENT
-----------------------------------------------------------------------------------------*/
export const createFleetVehicleContractRentAPI = async (
  vehicleId: string,
  formValues: FleetVehicleContractRentEditFormValues,
  t: (key: string) => string
): Promise<FleetVehicleContractRent> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/${vehicleId}/contracts/rent`,
      method: "POST",
      body: transformFleetVehicleContractRentOnCreate(formValues, vehicleId),
    },
    {
      getMessage: {
        success: () =>
          `${t("admin.fleet.vehicle.contract.rent.create.message.success")}`,
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  invalidateDropdownCache(DropdownCacheKey.FleetContractRents);
  return transformFleetVehicleContractRent(response.data);
};

/*---------------------------------------------------------------------------------------
  EDIT - VEHICLE - CONTRACT RENT
-----------------------------------------------------------------------------------------*/
export const updateFleetVehicleContractRentAPI = async (
  id: string,
  vehicleId: string,
  formValues: FleetVehicleContractRentEditFormValues,
  t: (key: string) => string
): Promise<FleetVehicleContractRent> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/${vehicleId}/contracts/rent/${id}`,
      method: "PUT",
      body: transformFleetVehicleContractRentOnUpdate(formValues, vehicleId),
    },
    {
      getMessage: {
       success: () =>
          `${t("admin.fleet.vehicle.contract.rent.edit.message.success")}`,
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  invalidateDropdownCache(DropdownCacheKey.FleetContractRents);
  return transformFleetVehicleContractRent(response.data);
};

/*---------------------------------------------------------------------------------------
  DELETE - VEHICLE - CONTRACT RENT
-----------------------------------------------------------------------------------------*/
export const deleteFleetVehicleContractRentAPI = async (
  vehicleId: string,
  contractId: string,
  t: (key: string) => string
): Promise<boolean> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/${vehicleId}/contracts/rent/${contractId}`,
      method: "DELETE",
    },
    {
      getMessage: {
        success: () => t("admin.fleet.vehicle.contract.message.delete"),
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  invalidateDropdownCache(DropdownCacheKey.FleetContractRents);
  return response.data?.deleted;
};

/*---------------------------------------------------------------------------------------
   PREVIEW - CONTRACT RENT PDF
-----------------------------------------------------------------------------------------*/
export const getFleetVehicleContractRentPdfPreviewAPI = async (
  contractId: string
): Promise<string | null> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/contracts/rent/${contractId}/pdf/preview`,
      method: "GET",
      responseType: "blob",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  if (!response?.data) return null;

  const blob = new Blob([response.data], { type: "application/pdf" });
  return URL.createObjectURL(blob);
};

/*---------------------------------------------------------------------------------------
  TOGGLE - CONTRACT RENT STATUS HISTORY
-----------------------------------------------------------------------------------------*/
export const toggleContractRentStatusHistoryAPI = async (
  contractId: string,
  statusId: string,
  checked: boolean
): Promise<FleetVehicleContractRent> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/contracts/rent/${contractId}/status-history/${statusId}`,
      method: "PUT",
      body: { checked },
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformFleetVehicleContractRent(response.data);
};

/*---------------------------------------------------------------------------------------
  SEND - CONTRACT RENT - EMAIL
-----------------------------------------------------------------------------------------*/
export const sendFleetVehicleContractRentEmailAPI = async (
  contractId: string,
  emailData: EmailSendData
): Promise<boolean> =>
  sendContractEmail(`/v1/fleet/vehicles/contracts/rent/${contractId}/send-email`, emailData);

/*---------------------------------------------------------------------------------------
  GET - CONTRACT RENT - STAVKE ZA RAČUN
-----------------------------------------------------------------------------------------*/
/**
 * Stavke kojima se puni novi račun najma — poslužitelj vraća samo ponavljajuće (najamnina).
 * Kazne i jednokratne usluge unose se ručno na računu.
 */
export const getContractRentInvoiceItemsAPI = async (
  contractId: string
): Promise<InvoiceOutgoingItemRow[]> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/contracts/rent/${contractId}/invoice-items`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return response.data?.map(transformInvoicePrefillItemRow) ?? [];
};
